/* ============================================================
   WUF Signals — first-party behavioral layer for Homepage 2.0
   Records browsing signals to localStorage (on-device only) and
   exposes hydration helpers used by the WUF · sections.
   No cookies, no third parties, nothing leaves the browser.
   ============================================================ */
(function () {
  'use strict';
  if (window.WufSignals) return; // idempotent

  var KEY = 'wuf.signals.v1';
  var FAV_KEY = 'wuf.favs.v1';
  var MAX_EVENTS = 120;
  var MONEY_FORMAT = (window.WufConfig && window.WufConfig.moneyFormat) || '${{amount}}';

  function read() {
    try { return JSON.parse(localStorage.getItem(KEY)) || { events: [] }; }
    catch (e) { return { events: [] }; }
  }
  function write(state) {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }

  function record(type, data) {
    var state = read();
    state.events.push(Object.assign({ t: type, at: Date.now() }, data));
    if (state.events.length > MAX_EVENTS) state.events = state.events.slice(-MAX_EVENTS);
    write(state);
  }

  /* ---------- aggregation ---------- */
  function recentEvents(type, days) {
    var cutoff = Date.now() - (days || 30) * 864e5;
    return read().events.filter(function (e) { return e.t === type && e.at > cutoff; });
  }

  // Top groups of viewed products, keyed by product_type (fallback: collection title)
  // `by` = 'count' (default: most-viewed group first) or 'recent' (the group you were last in first).
  // Each group's img/url/handle track the LATEST view in it, so "pick up where you left off" shows the last product.
  function topGroups(limit, by) {
    var views = recentEvents('pv', 30);
    var groups = {};
    views.forEach(function (e) {
      var key = e.ptype || e.coll || 'Other';
      if (!groups[key]) groups[key] = { key: key, count: 0, handles: [], url: e.collUrl || null, img: null, last: 0, lastHandle: null };
      groups[key].count++;
      if (groups[key].handles.indexOf(e.h) === -1) groups[key].handles.push(e.h);
      if (e.at >= groups[key].last) {
        groups[key].last = e.at;
        if (e.img) groups[key].img = e.img;
        if (e.collUrl) groups[key].url = e.collUrl;
        groups[key].lastHandle = e.h || null;
      }
    });
    return Object.keys(groups).map(function (k) { return groups[k]; })
      .sort(function (a, b) { return by === 'recent' ? (b.last - a.last) : (b.count - a.count || b.last - a.last); })
      .slice(0, limit || 4);
  }

  function recentProductIds(limit) {
    var seen = [], out = [];
    recentEvents('pv', 30).reverse().forEach(function (e) {
      if (e.id && seen.indexOf(e.id) === -1) { seen.push(e.id); out.push(e.id); }
    });
    return out.slice(0, limit || 3);
  }

  function topCollections(limit) {
    var counts = {};
    recentEvents('cv', 30).forEach(function (e) {
      if (!e.h) return;
      counts[e.h] = (counts[e.h] || 0) + 1;
    });
    return Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; }).slice(0, limit || 5);
  }

  function topVendors(limit) {
    var counts = {};
    recentEvents('pv', 30).forEach(function (e) {
      if (!e.v) return;
      counts[e.v] = (counts[e.v] || 0) + 1;
    });
    return Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; })
      .slice(0, limit || 3).map(function (v) { return { vendor: v, count: counts[v] }; });
  }

  // last distinct viewed products per group, newest first
  function groupProducts(groupKey, limit) {
    var seen = [], out = [];
    recentEvents('pv', 30).reverse().forEach(function (e) {
      var key = e.ptype || e.coll || 'Other';
      if (key !== groupKey || !e.h || seen.indexOf(e.h) !== -1) return;
      seen.push(e.h); out.push(e.h);
    });
    return out.slice(0, limit || 3);
  }

  /* ---------- brand values (exported from brand metaobjects) ---------- */
  var brandMapPromise = null;
  function brandMap() {
    if (!brandMapPromise) {
      var url = (window.WufConfig && window.WufConfig.brandValuesUrl);
      brandMapPromise = url
        ? fetch(url).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; })
        : Promise.resolve({});
    }
    return brandMapPromise;
  }
  function valuePillClass(label) {
    var l = String(label).toLowerCase();
    if (l.indexOf('family') !== -1) return 'wuf-vpill--family';
    if (l.indexOf('female') !== -1) return 'wuf-vpill--female';
    if (l.indexOf('give') !== -1 || l.indexOf('charit') !== -1) return 'wuf-vpill--give';
    return 'wuf-vpill--small';
  }
  function valuePillHtml(label) {
    return '<span class="wuf-vpill ' + valuePillClass(label) + '">' + label + '</span>';
  }

  /* ---------- trending (90-day orders, exported by studio) ---------- */
  var trendPromise = null;
  function trending() {
    if (!trendPromise) {
      var url = (window.WufConfig && window.WufConfig.trendingUrl);
      trendPromise = url
        ? fetch(url).then(function (r) { return r.ok ? r.json() : { scores: {} }; }).catch(function () { return { scores: {} }; })
        : Promise.resolve({ scores: {} });
    }
    return trendPromise;
  }

  // Rank products: shopper affinity (their vendors/types/views) + 90-day trend score.
  function rank(products, trendScores) {
    var pv = recentEvents('pv', 30);
    var vend = {}, typ = {}, seenH = {};
    pv.forEach(function (e) {
      if (e.v) vend[e.v] = (vend[e.v] || 0) + 1;
      if (e.ptype) typ[e.ptype] = (typ[e.ptype] || 0) + 1;
      if (e.h) seenH[e.h] = 1;
    });
    var maxTrend = 1;
    Object.keys(trendScores || {}).forEach(function (k) { if (trendScores[k] > maxTrend) maxTrend = trendScores[k]; });
    return products.map(function (p, i) {
      var score = 0;
      if (p.vendor && vend[p.vendor]) score += 3 * Math.min(vend[p.vendor], 3);
      var pt = p.product_type || p.type;
      if (pt && typ[pt]) score += 2 * Math.min(typ[pt], 3);
      if (p.handle && seenH[p.handle]) score += 4;
      if (trendScores && trendScores[p.handle]) score += 6 * (trendScores[p.handle] / maxTrend);
      return { p: p, score: score, i: i };
    }).sort(function (a, b) { return b.score - a.score || a.i - b.i; }).map(function (x) { return x.p; });
  }

  // One product per vendor first (in ranked order); once every vendor has a pick,
  // backfill remaining slots with each vendor's next-best product, still rank-ordered.
  function diversifyByVendor(ranked, n) {
    var byVendor = {}, order = [];
    ranked.forEach(function (p) {
      var v = p.vendor || '';
      if (!byVendor[v]) { byVendor[v] = []; order.push(v); }
      byVendor[v].push(p);
    });
    var out = [], idx = {};
    order.forEach(function (v) { idx[v] = 0; });
    var round = 0, added = true;
    while (out.length < n && added) {
      added = false;
      for (var i = 0; i < order.length && out.length < n; i++) {
        var v = order[i], list = byVendor[v];
        if (idx[v] === round && list[round]) { out.push(list[round]); idx[v] = round + 1; added = true; }
      }
      round++;
    }
    return out;
  }

  // Fetch a collection pool and return top-n ranked products, one per vendor first.
  function rankedCollection(handle, n, pool) {
    return Promise.all([collectionProducts(handle, pool || 12), trending()]).then(function (r) {
      return diversifyByVendor(rank(r[0], (r[1] || {}).scores), n || 4);
    });
  }

  /* ---------- favorites (wishlist hearts) ---------- */
  function favs() {
    try { return JSON.parse(localStorage.getItem(FAV_KEY)) || []; } catch (e) { return []; }
  }
  function toggleFav(handle) {
    var list = favs(), i = list.indexOf(handle);
    if (i === -1) list.push(handle); else list.splice(i, 1);
    try { localStorage.setItem(FAV_KEY, JSON.stringify(list)); } catch (e) {}
    return i === -1;
  }

  /* ---------- hydration helpers ---------- */
  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }
  function productByHandle(handle) { return fetchJSON('/products/' + handle + '.js'); }
  function recommendations(productId, limit, intent) {
    return fetchJSON('/recommendations/products.json?product_id=' + productId +
      '&limit=' + (limit || 6) + '&intent=' + (intent || 'related'))
      .then(function (d) { return d.products || []; });
  }
  function collectionProducts(handle, limit) {
    return fetchJSON('/collections/' + handle + '/products.json?limit=' + (limit || 6))
      .then(function (d) {
        return (d.products || []).filter(function (p) {
          return p.available !== false && p.variants && p.variants.some(function (v) { return v.available; });
        });
      });
  }

  // Normalize price to cents. Handles /products/{h}.js + /recommendations (cents)
  // AND /collections/{h}/products.json (string dollars on variants).
  function cents(p) {
    if (p == null) return null;
    var v = (typeof p === 'object') ? (p.price != null ? p.price : (p.variants && p.variants[0] && p.variants[0].price)) : p;
    if (v == null) return null;
    if (typeof v === 'string') { var f = parseFloat(v); return isNaN(f) ? null : Math.round(f * 100); }
    return v;
  }
  function money(v) {
    var c = cents(v);
    if (c == null) return '';
    var amount = (c / 100).toFixed(2);
    return MONEY_FORMAT.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  /* ---------- card renderer (matches snippets/wuf-product-card.liquid) ---------- */
  function imgUrl(src, w) {
    if (!src) return '';
    return src.replace(/(\.[a-z]{3,4})(\?|$)/i, '_' + w + 'x$1$2');
  }
  function renderCard(p, opts) {
    opts = opts || {};
    // Accepts both /products/{h}.js shape and /recommendations shape
    var img = p.featured_image || (p.images && p.images[0]) || p.image || '';
    if (img && img.src) img = img.src;
    var price = cents(p) || 0;
    var compare = cents(p.compare_at_price) || 0;
    var url = p.url || ('/products/' + p.handle);
    var variantId = p.variants && p.variants[0] && p.variants[0].id;
    var isFav = favs().indexOf(p.handle) !== -1;
    var pill = '';
    if (opts.pill) pill = '<span class="wuf-pill">' + opts.pill + '</span>';
    else if (compare > price) {
      var pct = Math.round(100 - (price / compare) * 100);
      pill = '<span class="wuf-pill wuf-pill--deal">−' + pct + '%</span>';
    }
    return '<div class="wuf-pcard" data-handle="' + p.handle + '">' +
      '<a class="wuf-pcard__img" href="' + url + '">' +
      (img ? '<img src="' + imgUrl(img, 360) + '" alt="" loading="lazy" width="360" height="480">' : '') +
      pill +
      '<button type="button" class="wuf-fav' + (isFav ? ' is-on' : '') + '" data-wuf-fav="' + p.handle + '" aria-label="Save to favorites"><svg viewBox="0 0 24 24"><path d="M12 20.5s-8-4.9-8-11a4.6 4.6 0 0 1 8-3 4.6 4.6 0 0 1 8 3c0 6.1-8 11-8 11z"/></svg></button>' +
      (variantId ? '<button type="button" class="wuf-quick" data-wuf-atc="' + variantId + '" aria-label="Add to cart"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg></button>' : '') +
      '</a>' +
      (p.vendor ? '<div class="wuf-pcard__brand">' + p.vendor + '</div>' : '') +
      '<div class="wuf-pcard__name">' + (p.title || '') + '</div>' +
      '<div class="wuf-pcard__price">' + money(price) +
      (compare > price ? ' <s>' + money(compare) + '</s>' : '') + '</div>' +
      '</div>';
  }

  /* ---------- quick add + toast ---------- */
  var toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wuf-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-show'); }, 2000);
  }

  document.addEventListener('click', function (ev) {
    var atc = ev.target.closest && ev.target.closest('[data-wuf-atc]');
    if (atc) {
      ev.preventDefault(); ev.stopPropagation();
      atc.classList.add('is-loading');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(atc.getAttribute('data-wuf-atc')), quantity: 1 })
      }).then(function (r) {
        atc.classList.remove('is-loading');
        if (!r.ok) throw new Error('atc failed');
        record('atc', { vid: atc.getAttribute('data-wuf-atc') });
        toast('Added to cart');
        document.dispatchEvent(new CustomEvent('wuf:cart:added'));
      }).catch(function () {
        atc.classList.remove('is-loading');
        toast('Could not add — try the product page');
      });
      return;
    }
    var fav = ev.target.closest && ev.target.closest('[data-wuf-fav]');
    if (fav) {
      ev.preventDefault(); ev.stopPropagation();
      var on = toggleFav(fav.getAttribute('data-wuf-fav'));
      fav.classList.toggle('is-on', on);
      toast(on ? 'Saved' : 'Removed');
    }
  });

  /* ---------- scroll reveal ---------- */
  function initReveals(root) {
    var els = (root || document).querySelectorAll('.wuf-reveal:not(.is-in)');
    if (window.Shopify && window.Shopify.designMode) {
      Array.prototype.forEach.call(els, function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!('IntersectionObserver' in window)) {
      els.forEach ? els.forEach(function (el) { el.classList.add('is-in'); }) : null;
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    Array.prototype.forEach.call(els, function (el) {
      io.observe(el);
      // Safety net: never leave content permanently invisible if the observer
      // misses (layout shift from late-loading images/fonts, engine quirks, etc).
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) { el.classList.add('is-in'); io.unobserve(el); }
      setTimeout(function () {
        if (!el.classList.contains('is-in')) { el.classList.add('is-in'); io.unobserve(el); }
      }, 2500);
    });
  }

  /* ---------- page-type auto-tracking ---------- */
  function autoTrack() {
    var meta = window.WufPage || {};
    if (meta.type === 'product' && meta.product) {
      record('pv', {
        id: meta.product.id, h: meta.product.handle, ptype: meta.product.type,
        v: meta.product.vendor, img: meta.product.image, coll: meta.collection || null,
        collUrl: meta.collectionUrl || null
      });
    } else if (meta.type === 'collection' && meta.collection) {
      record('cv', { h: meta.collection.handle, title: meta.collection.title });
    } else if (meta.type === 'search' && meta.terms) {
      record('sq', { q: String(meta.terms).slice(0, 60) });
    }
  }

  window.WufSignals = {
    record: record,
    topGroups: topGroups,
    topVendors: topVendors,
    groupProducts: groupProducts,
    brandMap: brandMap,
    valuePillHtml: valuePillHtml,
    recentProductIds: recentProductIds,
    topCollections: topCollections,
    favs: favs,
    productByHandle: productByHandle,
    recommendations: recommendations,
    collectionProducts: collectionProducts,
    rank: rank,
    rankedCollection: rankedCollection,
    trending: trending,
    renderCard: renderCard,
    money: money,
    cents: cents,
    toast: toast,
    initReveals: initReveals
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { autoTrack(); initReveals(); });
  } else { autoTrack(); initReveals(); }

  /* theme editor: re-run reveal on every section swap so edited sections don't stay hidden */
  document.addEventListener('shopify:section:load', function (ev) { initReveals(ev.target); });
  document.addEventListener('shopify:section:reorder', function () { initReveals(); });
})();

/* ---------- Trackpad swipe → Kalles Flickity carousels ----------
   Flickity only listens for pointer drag; a laptop trackpad swipe is a wheel event, so the
   featured-collection rows could only be moved with the arrows. Horizontal wheel intent over a
   .flickityt4s carousel now advances it one group, one move per gesture (600ms lock), matching the
   one-card-per-swipe feel of the native-scroll rows. Vertical wheel is left alone. */
(function () {
  if (window.__wufFlickWheel) return; window.__wufFlickWheel = true;
  var locked = new WeakMap();
  function instance(el) {
    var F = window.Flickityt4s || window.Flickity;
    try { if (F && typeof F.data === 'function') { var i = F.data(el); if (i) return i; } } catch (e) {}
    return el.flickityt4s || el.flickity || null;
  }
  function move(el, dir) {
    var inst = instance(el);
    if (inst && typeof inst.next === 'function') { dir > 0 ? inst.next() : inst.previous(); return true; }
    var btn = el.querySelector(dir > 0 ? '.flickityt4s-prev-next-button.next, .flickity-prev-next-button.next' : '.flickityt4s-prev-next-button.previous, .flickity-prev-next-button.previous');
    if (btn) { btn.click(); return true; }
    return false;
  }
  // One card per move, like the native-scroll rows: Kalles inits these with grouped cells (a "page" is
  // the visible 3) and Flickity's drag picks its landing slide from momentum, so a flick could skip 3+.
  // Ungroup (arrows, dots and the wheel bridge then step one card) and cap a drag to ±1 slide.
  function tune(el) {
    if (el.__wufTuned) return; var inst = instance(el); if (!inst) return;
    el.__wufTuned = true;
    try {
      if (inst.options.groupCells && inst.options.groupCells !== 1) { inst.options.groupCells = 1; inst.resize(); }
      var orig = inst.dragEndRestingSelect;
      inst.dragEndRestingSelect = function () {
        if (typeof this.dragStartPosition !== 'number' || typeof this.velocity !== 'number') return orig.apply(this, arguments);
        var moved = this.dragX - this.dragStartPosition;
        if (Math.abs(moved) < 12 && Math.abs(this.velocity) < 1) return this.selectedIndex;
        var dir = this.velocity ? (this.velocity < 0 ? 1 : -1) : (moved < 0 ? 1 : -1);
        return Math.max(0, Math.min(this.slides.length - 1, this.selectedIndex + dir));
      };
    } catch (e) {}
  }
  // Featured-collection rows: replace Flickity with NATIVE scroll-snap (same mechanics as the promo row).
  // The wheel bridge above is a heuristic over Flickity and a long swipe can always fool it; native
  // scrolling lets macOS/iOS decide what a gesture is — one card per swipe, any pace. Flickity is
  // destroyed and its hooks removed so Kalles' resize observer / section:load can't re-init it.
  function nativize(el) {
    if (el.__wufNative) return; var inst = instance(el); if (!inst) return;
    el.__wufNative = true;
    try { inst.destroy(); } catch (e) {}
    // Kalles' resize observer / section hooks still call $(el).flickityt4s('resize') on this row; give jQuery a
    // no-op instance so those calls are silent instead of logging "flickityt4s not initialized".
    try { var $ = window.jQuery || window.$; if ($ && $.data) { var noop = function () {}; $.data(el, 'flickityt4s', { resize: noop, reloadCells: noop, select: noop, next: noop, previous: noop, destroy: noop, reposition: noop }); } } catch (e) {}
    el.classList.remove('flickityt4s'); el.removeAttribute('data-flickityt4s-js'); el.removeAttribute('data-t4s-resizeobserver');
    el.querySelectorAll('.flickityt4s-button, .flickityt4s-page-dots').forEach(function (b) { b.remove(); });
    el.classList.add('wuf-native-rail');
    if (el.closest('.t4s-tab-content2')) el.classList.add('wuf-native-rail--free'); // tabbed rows: free momentum scroll on phones (Paul likes that feel), one-card snap on desktop
    var wrap = el.parentElement; if (!wrap) return;
    wrap.classList.add('wuf-native-rail-wrap');
    [['prev', 'M15 5l-7 7 7 7', 'Scroll back'], ['next', 'M9 5l7 7-7 7', 'Scroll forward']].forEach(function (a) {
      var b = document.createElement('button'); b.type = 'button'; b.className = 'wuf-arrow wuf-arrow--' + a[0]; b.setAttribute('aria-label', a[2]);
      b.innerHTML = '<svg viewBox="0 0 24 24"><path d="' + a[1] + '"/></svg>';
      b.addEventListener('click', function () { var c = el.firstElementChild; var w = c ? c.getBoundingClientRect().width : 280; el.scrollBy({ left: (a[0] === 'prev' ? -1 : 1) * w, behavior: 'smooth' }); });
      wrap.appendChild(b);
    });
  }
  // Featured rows + the product rows inside tabbed collections (each tab's row renders lazily when opened;
  // the tab HEADER strip is also a Flickity and is deliberately left alone).
  var NATIVE_SEL = '.t4s-featured-products .flickityt4s, .t4s-tabs-collection .t4s-tab-content2 .flickityt4s';
  function tuneAll() {
    document.querySelectorAll(NATIVE_SEL).forEach(nativize);
    document.querySelectorAll('.t4s_bk_flickity .flickityt4s').forEach(function (el) { if (!el.matches(NATIVE_SEL) && !el.closest('.t4s-tabs-ul')) tune(el); });
  }
  if (document.readyState === 'complete') tuneAll(); else window.addEventListener('load', tuneAll);
  setTimeout(tuneAll, 2500); setTimeout(tuneAll, 6000); // late inits (lazy sections)
  document.addEventListener('shopify:section:load', function () { setTimeout(tuneAll, 300); });
  // Tab switches render a new row, then Kalles inits Flickity on it a beat later: watch for the markup and retry
  var mo = new MutationObserver(function () { [300, 900, 2000].forEach(function (ms) { setTimeout(tuneAll, ms); }); });
  document.querySelectorAll('.t4s-tabs-collection').forEach(function (sec) { mo.observe(sec, { childList: true, subtree: true }); });
  document.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('[data-t4s-tab-item]')) [300, 900, 2000].forEach(function (ms) { setTimeout(tuneAll, ms); }); }, true);
  document.addEventListener('wheel', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('.flickityt4s') : null;
    if (el) tune(el);
    if (!el) return;
    var dx = e.deltaX, dy = e.deltaY;
    if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return; // vertical or too faint: let the page scroll
    e.preventDefault(); // stop the browser's back/forward swipe while over the carousel
    // A trackpad swipe streams wheel events for 1s+ as its inertia decays. Treat any run of events
    // without a 250ms gap as ONE gesture and act only on its first event; a timed lock let the tail
    // of a swipe register as a second swipe.
    // ...but a second swipe often starts INSIDE the previous tail, so a pure time gap made consecutive
    // swipes impossible. In a tail the magnitudes only shrink, so a magnitude that jumps back up
    // (>= 2x the previous event and >= 18) is a new swipe. Direction flips are new swipes too.
    // Friction: a tail isn't perfectly smooth, so a late jitter (prev tiny, this one bigger) could read as a
    // swipe → 2 cards. Re-acceleration must now be clearly deliberate (>= 22 and >= 2.2x the previous event),
    // and no two moves can happen within 220ms regardless. Tuned lighter per Paul: 'should feel like Apple's swipe'.
    // A swipe has two phases: finger-driven (deltas RAMP UP) then inertia (deltas only shrink). A rise
    // is new input only once the current gesture has already decayed (fell under half its peak) —
    // otherwise the ramp-up of a single swipe read as 2-3 swipes.
    var now = Date.now(), st = locked.get(el) || { t: 0, mag: 0, dir: 0, moved: 0, peak: 0, decayed: false };
    var mag = Math.abs(dx), dir = dx > 0 ? 1 : -1;
    var gap = now - st.t >= 250, flip = dir !== st.dir;
    var rise = st.decayed && mag >= 22 && mag >= st.mag + 10;
    var fresh = gap || flip || rise;
    var peak = fresh ? mag : Math.max(st.peak, mag);
    var decayed = fresh ? false : (st.decayed || mag < peak * 0.5);
    var moved = st.moved;
    if (fresh && now - st.moved >= 220) { move(el, dir); moved = now; }
    locked.set(el, { t: now, mag: mag, dir: dir, moved: moved, peak: peak, decayed: decayed });
  }, { passive: false });
})();
